<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="640" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card class="cu">
      <div class="fdlg__form">
        <div class="fdlg__head cu__head">
          <v-icon class="cu__mark" aria-hidden="true">mdi-alert-octagon-outline</v-icon>
          <div>
            <h2 class="fdlg__title">{{ options.title }}</h2>
            <p class="fdlg__sub cu__sub">Este deja în uz. Citiți ce se schimbă și ce nu înainte să confirmați.</p>
          </div>
        </div>

        <v-card-text ref="body" class="fdlg__body">
          <div v-if="options.from || options.to" class="cu__rename">
            <span class="cu__old">{{ options.from }}</span>
            <v-icon class="cu__arrow" aria-label="devine">mdi-arrow-right</v-icon>
            <span class="cu__new">{{ options.to }}</span>
          </div>

          <div v-if="parts.length" class="cu__usage" aria-label="Unde se folosește">
            <div v-for="p in parts" :key="p.key" class="cu__tile">
              <span class="cu__n">{{ p.n }}</span>
              <span class="cu__label">{{ p.label }}</span>
              <span v-if="p.sub" class="cu__tile-sub">{{ p.sub }}</span>
            </div>
          </div>

          <section v-if="options.changes && options.changes.length" class="form-section">
            <div class="form-section__label cu__label-changes">
              <v-icon small>mdi-swap-horizontal</v-icon>Ce se schimbă
            </div>
            <ul class="cu__list">
              <li v-for="(line, i) in options.changes" :key="i">{{ line }}</li>
            </ul>
          </section>

          <section v-if="options.keeps && options.keeps.length" class="form-section">
            <div class="form-section__label">
              <v-icon small>mdi-lock-outline</v-icon>Ce NU se schimbă
            </div>
            <ul class="cu__list">
              <li v-for="(line, i) in options.keeps" :key="i">{{ line }}</li>
            </ul>
          </section>

          <section class="form-section cu__confirm">
            <template v-if="options.typeName">
              <p class="cu__confirm-text">
                Ca să confirmați, scrieți denumirea nouă: <strong>{{ options.to }}</strong>
              </p>
              <v-text-field
                v-model="typed"
                label="Denumirea nouă"
                prepend-inner-icon="mdi-form-textbox"
                outlined
                dense
                autocomplete="off"
                :hint="typedMatches ? 'Corect' : ''"
                persistent-hint
                @keyup.enter="typedMatches && finish(true)"
              ></v-text-field>
            </template>
            <v-checkbox
              v-else
              v-model="understood"
              color="error"
              hide-details
              class="cu__check"
              label="Am înțeles ce se schimbă și ce nu"
            ></v-checkbox>
          </section>
        </v-card-text>

        <div class="fdlg__foot">
          <v-spacer></v-spacer>
          <v-btn text large @click="finish(false)">Anulare</v-btn>
          <v-btn large depressed color="error" :disabled="!canConfirm" @click="finish(true)">
            <v-icon left>mdi-alert-outline</v-icon>Da, modifică
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import * as helper from "@/helper/helper";
import { plain, usageParts } from "./format";

/**
 * The heavy warning before renaming something already in use. ask(options) resolves true when the user
 * confirmed. options: { title, from, to, usage, changes: [], keeps: [], typeName } — typeName asks the
 * user to type the new name, otherwise a tick on "Am înțeles" is enough.
 */
export default {
  name: "ConfirmUsedDialog",
  data() {
    return {
      dialog: false,
      options: {},
      typed: "",
      understood: false,
      resolve: null,
    };
  },
  computed: {
    parts() {
      return usageParts(this.options.usage);
    },
    typedMatches() {
      return !!this.typed && plain(this.typed) === plain(this.options.to);
    },
    canConfirm() {
      return this.options.typeName ? this.typedMatches : this.understood;
    },
  },
  methods: {
    ask(options) {
      this.options = options || {};
      this.typed = "";
      this.understood = false;
      this.dialog = true;
      helper.scrollToTop(this);
      return new Promise((resolve) => {
        this.resolve = resolve;
      });
    },
    finish(accepted) {
      this.dialog = false;
      if (this.resolve) this.resolve(accepted);
      this.resolve = null;
    },
  },
};
</script>

<style scoped>
.fdlg__head.cu__head {
  background: var(--brick);
  align-items: center;
}
.cu__mark.v-icon {
  flex: none;
  font-size: 44px;
  color: #fff;
}
.fdlg__sub.cu__sub {
  color: #ffe1de;
}
.cu__rename {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  margin-bottom: 16px;
  padding: 14px 16px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid var(--line);
  font-size: 1.05rem;
  font-weight: 700;
  overflow-wrap: anywhere;
}
.cu__old {
  color: var(--ink-3);
  text-decoration: line-through;
}
.cu__new {
  color: var(--ink);
}
.cu__arrow.v-icon {
  color: var(--brick);
}
.cu__usage {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}
.cu__tile {
  display: flex;
  flex-direction: column;
  padding: 12px 14px;
  border-radius: 14px;
  background: var(--brick-bg);
  border: 1px solid #f1c3bf;
  color: var(--brick);
}
.cu__n {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 2rem;
  line-height: 1;
}
.cu__label {
  margin-top: 4px;
  font-weight: 700;
}
.cu__tile-sub {
  font-size: 0.88rem;
  color: #8f2a24;
}
.cu__label-changes .v-icon {
  color: var(--brick) !important;
}
.cu__list {
  margin: -4px 0 12px;
  padding-left: 20px;
  line-height: 1.5;
}
.cu__list li + li {
  margin-top: 4px;
}
.cu__confirm {
  border-color: #f1c3bf;
  padding-bottom: 10px;
}
.cu__confirm-text {
  margin: 0 0 12px;
  overflow-wrap: anywhere;
}
.cu__check {
  margin: 0 0 6px;
}
</style>
